import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileCode, FileText, CheckCircle2, ShieldCheck, RefreshCw, Undo2, Mail, Pencil, Printer, PackageCheck } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { downloadDoc, printDoc } from "@/lib/downloadDoc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export default function EInvoiceDetail() {
  const { id } = useParams();
  const { t, fmtCurrency, fmtDate } = useI18n();
  const { can, businessCapabilities } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");

  const { data: ei, isLoading } = useQuery({
    queryKey: ["einvoice", id],
    queryFn: async () => (await api.get(`/einvoice/invoices/${id}`)).data,
  });
  const { data: emailStatus } = useQuery({
    queryKey: ["einvoice-email-status"],
    queryFn: async () => (await api.get("/einvoice/email/status")).data,
  });

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); qc.invalidateQueries({ queryKey: ["einvoice", id] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(""); }
  };

  const finalize = () => act("finalize", async () => {
    const { data } = await api.post(`/einvoice/invoices/${id}/finalize`);
    if (data.invoice_format === "standard") toast.success(t("einvoice.finalized_standard"));
    else if (data.production_valid) toast.success(t("einvoice.finalized_valid"));
    else toast.warning(t("einvoice.finalized_not_valid"));
  });
  const validate = () => act("validate", async () => {
    const { data } = await api.post(`/einvoice/invoices/${id}/validate`);
    if (data.has_errors) toast.error(t("einvoice.preflight_errors"));
    else toast.success(t("einvoice.preflight_ok"));
  });
  const creditNote = () => act("credit", async () => {
    const { data } = await api.post(`/einvoice/invoices/${id}/credit-note`, { reason: t("einvoice.credit_default_reason") });
    toast.success(t("einvoice.credit_created"));
    navigate(`/finance/einvoices/${data.id}`);
  });
  const sendEmail = () => act("email", async () => {
    const key = globalThis.crypto?.randomUUID?.() || `${Date.now()}`;
    await api.post(`/einvoice/invoices/${id}/send-email`, { idempotency_key: key });
    toast.success(t("einvoice.email_sent"));
  });

  if (isLoading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  const report = ei.validation_report || {};
  const isStandard = ei.invoice_format === "standard";
  const pv = ei.production_valid;
  const isHistorical = ei.status === "historical";
  const isFinal = ei.status === "finalized" || ei.status === "exported";
  const isPrintable = isFinal || isHistorical;
  const requestedReturnTo = searchParams.get("returnTo") || "";
  const returnTo = requestedReturnTo.startsWith("/") && !requestedReturnTo.startsWith("//")
    ? requestedReturnTo : (ei.customer_id ? `/customers/${ei.customer_id}` : "/finance/einvoices");
  const errors = (report.issues || []).filter((i) => i.severity === "error");
  const printInvoice = () => act("print", async () => {
    await printDoc(`/einvoice/invoices/${id}/print-copy?stationery=standard`);
  });
  const printOnStationery = () => act("print-stationery", async () => {
    await printDoc(`/einvoice/invoices/${id}/print-copy?stationery=preprinted`);
  });

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate(returnTo)} data-testid="einvoice-detail-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>
      <PageHeader
        title={(ei.doc_type === "credit_note" ? t("einvoice.credit_note") + " " : "") + (ei.number || t("einvoice.draft_no"))}
        subtitle={ei.buyer_name}
        actions={<StatusBadge status={ei.status} labelKey={`einvoice.status.${ei.status}`} testId="einvoice-detail-status" />} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-2">
          <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.positions")}</h3>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400">
              <th className="py-1">{t("einvoice.line_desc")}</th><th className="text-right">{t("einvoice.line_qty")}</th>
              {!isHistorical && <th className="text-right">{t("einvoice.line_price")}</th>}{!isHistorical && <th className="text-right">{t("einvoice.line_vat")}</th>}
              <th className="text-right">{t("einvoice.line_net")}</th></tr></thead>
            <tbody>
              {(ei.totals?.lines || []).map((l, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-1.5">{l.description}</td><td className="text-right tabular-nums">{Number(l.quantity) ? l.quantity : "—"}</td>
                  {!isHistorical && <td className="text-right tabular-nums">{fmtCurrency(l.unit_price)}</td>}{!isHistorical && <td className="text-right tabular-nums">{l.vat_rate}%</td>}
                  <td className="text-right tabular-nums">{Number(l.line_net) ? fmtCurrency(l.line_net) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {isHistorical ? <div className="mt-3 border-t border-slate-200 pt-3 text-right text-sm font-semibold dark:border-slate-700">{t("einvoice.archive_amount")}: <span className="tabular-nums">{fmtCurrency(ei.totals?.archive_amount ?? ei.totals?.gross_total)}</span></div> : <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-right text-sm dark:border-slate-700">
            <div>{t("einvoice.net")}: <span className="tabular-nums">{fmtCurrency(ei.totals?.net_total)}</span></div>
            <div className="text-slate-500">{t("einvoice.tax")}: <span className="tabular-nums">{fmtCurrency(ei.totals?.tax_total)}</span></div>
            <div className="font-semibold">{t("einvoice.total")}: <span className="tabular-nums">{fmtCurrency(ei.totals?.gross_total)}</span></div>
          </div>}
        </Card>

        <div className="space-y-4">
          {isFinal && ei.inventory_posting_status === "posted" && (
            <Card className="border-emerald-300 bg-emerald-50/70 p-4 dark:border-emerald-800 dark:bg-emerald-950/20" data-testid="einvoice-inventory-posted">
              <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.inventory")}</h3>
              <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-300"><PackageCheck className="h-4 w-4" />{t("einvoice.inventory_posted")}</div>
              <p className="mt-1 text-xs text-slate-500">{t("einvoice.inventory_posted_hint")}</p>
            </Card>
          )}
          {isFinal && ei.payment && (
            <Card className={`p-4 ${ei.payment.is_paid ? "border-emerald-300 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-950/20" : ""}`}>
              <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.payment_status")}</h3>
              <div className="flex items-center gap-2 font-semibold">{ei.payment.is_paid && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}{t(`einvoice.payment_${ei.payment.status}`)}</div>
              <div className="mt-2 space-y-1 text-sm"><div className="flex justify-between"><span>{t("einvoice.paid_amount")}</span><span>{fmtCurrency(ei.payment.paid_amount)}</span></div><div className="flex justify-between"><span>{t("einvoice.open_amount")}</span><span>{fmtCurrency(ei.payment.open_amount)}</span></div>{ei.payment.last_payment_at && <div className="flex justify-between"><span>{t("einvoice.payment_date")}</span><span>{fmtDate(ei.payment.last_payment_at)}</span></div>}{ei.payment.methods?.length > 0 && <div className="flex justify-between"><span>{t("einvoice.payment_method")}</span><span>{ei.payment.methods.join(" / ")}</span></div>}</div>
            </Card>
          )}
          {isHistorical ? <Card className="border-amber-300 bg-amber-50/60 p-4 dark:border-amber-800 dark:bg-amber-950/20" data-testid="einvoice-archive-card">
            <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">{t("einvoice.archive_title")}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">{t("einvoice.archive_hint")}</p>
            <div className="mt-3 space-y-1 text-xs text-slate-500"><div>{t("einvoice.date")}: {fmtDate(ei.issue_date)}</div>{ei.vehicle_snapshot?.registration_plate && <div>{t("automotive.plate")}: {ei.vehicle_snapshot.registration_plate}</div>}{ei.vehicle_snapshot?.legacy_description && <div>{t("automotive.title")}: {ei.vehicle_snapshot.legacy_description}</div>}</div>
          </Card> : <Card className="p-4" data-testid="einvoice-validation-card">
            <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.validation")}</h3>
            {isFinal && isStandard ? (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="einvoice-validation-verdict">
                <CheckCircle2 className="h-4 w-4" />{t("einvoice.standard_finalized")}
              </div>
            ) : isFinal ? (
              <div className={`flex items-center gap-2 rounded-md p-2 text-sm ${pv ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"}`} data-testid="einvoice-validation-verdict">
                {pv ? <CheckCircle2 className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                {pv ? t("einvoice.production_valid") : t("einvoice.preflight_only")}
              </div>
            ) : errors.length ? (
              <div className="rounded-md bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-300">
                {errors.slice(0, 6).map((e, i) => <div key={i}>• {e.rule}: {e.message}</div>)}
              </div>
            ) : <p className="text-sm text-slate-500">{t("einvoice.not_validated")}</p>}
            <p className="mt-2 text-xs text-slate-400">{t("einvoice.level")}: {report.level || "—"}</p>
            {report.zugferd?.generated && <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><FileText className="h-3 w-3" />ZUGFeRD PDF/A-3 {report.zugferd.cii_production_valid ? "✓" : "·"}</p>}
          </Card>}

          {isFinal && ei.documents?.length > 0 && (
            <Card className="p-4">
              <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.documents")}</h3>
              <div className="space-y-2">
                {ei.documents.map((d) => (
                  <button key={d.id} onClick={() => downloadDoc(`/einvoice/invoices/${id}/documents/${d.id}/download`, d.filename)}
                    className="flex w-full items-center gap-2 rounded-md border border-slate-200 p-2 text-left text-sm hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800" data-testid={`einvoice-doc-${d.kind}`}>
                    {d.kind === "zugferd_pdf" || d.kind === "invoice_pdf" ? <FileText className="h-4 w-4 text-red-500" /> : <FileCode className="h-4 w-4 text-blue-500" />}
                    <span className="flex-1 truncate">{d.filename}</span>
                    <span className="text-xs text-slate-400">{d.kind === "zugferd_pdf" ? "PDF/A-3" : d.kind === "invoice_pdf" ? "PDF" : "XML"}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {isFinal && (
            <Card className="p-4">
              <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.email_delivery")}</h3>
              {ei.email_deliveries?.length > 0 ? <div className="space-y-1 text-sm">
                {ei.email_deliveries.slice(0, 3).map((delivery) => <div key={delivery.id} className="flex justify-between gap-2"><span className="truncate">{delivery.recipient}</span><span>{t(`einvoice.email_status_${delivery.status}`)}</span></div>)}
              </div> : <p className="text-sm text-slate-500">{t("einvoice.email_not_sent")}</p>}
              {!emailStatus?.configured && <p className="mt-2 text-xs text-amber-600">{t("einvoice.email_not_configured")}</p>}
            </Card>
          )}

          <Card className="p-4">
            <div className="flex flex-col gap-2">
              {!isFinal && ei.status !== "cancelled" && can("einvoice:create") && <Button variant="outline" onClick={() => navigate(`/finance/einvoices/${id}/edit`)} data-testid="einvoice-edit-button"><Pencil className="h-4 w-4" />{t("einvoice.edit_draft")}</Button>}
              {!isFinal && can("einvoice:validate") && <Button variant="outline" onClick={validate} disabled={!!busy} data-testid="einvoice-validate-button"><RefreshCw className="h-4 w-4" />{t(isStandard ? "einvoice.standard_check" : "einvoice.preflight")}</Button>}
              {!isFinal && ei.status !== "cancelled" && can("einvoice:finalize") && <Button onClick={finalize} disabled={!!busy} data-testid="einvoice-finalize-button"><CheckCircle2 className="h-4 w-4" />{t("einvoice.finalize")}</Button>}
              {isPrintable && <Button onClick={printInvoice} disabled={!!busy} data-testid="einvoice-print-button"><Printer className="h-4 w-4" />{t("einvoice.print_pdf")}</Button>}
              {isPrintable && businessCapabilities?.preprinted_forms && <Button variant="outline" onClick={printOnStationery} disabled={!!busy} data-testid="einvoice-print-stationery-button"><Printer className="h-4 w-4" />{t("einvoice.print_preprinted")}</Button>}
              {isFinal && ei.doc_type === "invoice" && can("einvoice:create") && <Button variant="outline" onClick={creditNote} disabled={!!busy} data-testid="einvoice-creditnote-button"><Undo2 className="h-4 w-4" />{t("einvoice.credit_note")}</Button>}
              {isFinal && can("einvoice:create") && <Button onClick={sendEmail} disabled={!!busy || !emailStatus?.configured} data-testid="einvoice-email-button"><Mail className="h-4 w-4" />{t("einvoice.send_email")}</Button>}
              {ei.order_id && <Button variant="outline" onClick={() => navigate(`/orders/${ei.order_id}`)}><ArrowLeft className="h-4 w-4" />{t("einvoice.back_to_order")}</Button>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
